import { useEffect, useState } from "react";
import { Users, FileText, ShoppingCart, Wallet, AlertTriangle, Boxes, TrendingUp, Package } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { money } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";

const CHART_COLORS = ["#0D9488", "#2DD4BF", "#0EA5E9", "#F59E0B", "#8B5CF6", "#EF4444", "#10B981", "#6366F1", "#EC4899"];

function Stat({ label, value, icon: Icon, danger }) {
  return (
    <div className={`rounded-xl border bg-card p-5 ${danger ? "border-destructive/40" : "border-border"}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="text-xs font-medium text-muted-foreground">{label}</span><Icon className={`h-4 w-4 ${danger ? "text-destructive" : "text-primary"}`} /></div>
      <p className={`mt-2 font-display text-2xl font-extrabold ${danger ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <AdminLayout title="Dashboard"><p className="text-muted-foreground">Loading...</p></AdminLayout>;

  return (
    <AdminLayout title="Dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Customers" value={d.total_customers} icon={Users} />
        <Stat label="New Requests" value={d.new_requests} icon={FileText} />
        <Stat label="Active Orders" value={d.active_orders} icon={ShoppingCart} />
        <Stat label="Today Collection" value={money(d.todays_collection)} icon={Wallet} />
        <Stat label="Monthly Collection" value={money(d.monthly_collection)} icon={TrendingUp} />
        <Stat label="Overdue Amount" value={money(d.overdue_amount)} icon={AlertTriangle} danger={d.overdue_amount > 0} />
        <Stat label="Stock Value" value={money(d.inventory_value)} icon={Boxes} />
        <Stat label="Low Stock Items" value={d.low_stock} icon={Package} danger={d.low_stock > 0} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <h3 className="mb-4 font-display font-semibold">Collections & Orders (6 months)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={d.charts.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              <Line type="monotone" dataKey="collections" stroke="#0D9488" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="orders" stroke="#F59E0B" strokeWidth={2} dot={false} yAxisId={0} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="mb-4 font-display font-semibold">Order Status</h3>
          {d.charts.status_distribution.length === 0 ? <p className="py-16 text-center text-sm text-muted-foreground">No orders yet</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={d.charts.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {d.charts.status_distribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Pending Requests" value={d.pending_requests} icon={FileText} />
        <Stat label="Processing Orders" value={d.processing_orders} icon={ShoppingCart} />
        <Stat label="Active Installments" value={d.active_installments} icon={Wallet} />
        <Stat label="Total Sales" value={money(d.total_sales)} icon={TrendingUp} />
      </div>
    </AdminLayout>
  );
}
